import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, mergeMap, takeUntil } from 'rxjs/operators'

import { extractErrorMessage } from 'utils/ErrorMessages'
import { URL_CONST } from 'utils/urlConsts'

import { COPY_SCENARIO_ASYNC } from 'containers/AwarenessCenter/Scenarios/constants'
import {
  selectIsTournament,
  selectSelectedScenarios,
} from 'containers/AwarenessCenter/WizardCampaign/steps/selectors'
import { ADD_SCENARIO_ASYNC, SAVE_SCENARIO_ASYNC } from 'containers/ScenarioForm/constants'
import { addToast } from 'containers/Toasts/actions'

import { CHANGE_DELIVERY, RESET_WIZARD, TOGGLE_SCENARIO } from '../constants'

import {
  autoSelectScenariosError,
  autoSelectScenariosSuccess,
  fetchCategoriesError,
  fetchCategoriesSuccess,
} from './actions'
import {
  AUTO_SELECT_SCENARIOS_ASYNC,
  CHANGE_ATTACK_VECTOR,
  CHANGE_CREATED_BY_FILTER,
  CHANGE_LEVEL_FILTER,
  CHANGE_SORT_OPTION,
  CHANGE_TYPE_FILTER,
  FETCH_CATEGORIES_ASYNC,
  PICK_CATEGORY,
  SEARCH_CHANGE,
  TYPE_VALUES,
} from './constants'
import { selectLocales, selectTemplateSettings, selectTypeFilter } from './selectors'

const fetchCategoriesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FETCH_CATEGORIES_ASYNC.INITIATE,
      SAVE_SCENARIO_ASYNC.SUCCESS,
      COPY_SCENARIO_ASYNC.SUCCESS,
      PICK_CATEGORY,
      SEARCH_CHANGE,
      CHANGE_SORT_OPTION,
      CHANGE_LEVEL_FILTER,
      CHANGE_TYPE_FILTER,
      CHANGE_CREATED_BY_FILTER,
      ADD_SCENARIO_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (action.type === CHANGE_SORT_OPTION && !action.payload.isFetchCategories) {
        return false
      }
      if (
        action.type === ADD_SCENARIO_ASYNC.SUCCESS ||
        action.type === SAVE_SCENARIO_ASYNC.SUCCESS ||
        action.type === COPY_SCENARIO_ASYNC.SUCCESS
      ) {
        return window.location.pathname.indexOf(URL_CONST.awarenessCenter.wizardCampaign) !== -1
      } else return true
    }),
    mergeMap(() =>
      api.wizardCampaign
        .getScenarios(state$.value.partnerOptions.selectedCompany?.value, {
          locales: selectLocales(state$.value),
          categoryId:
            state$.value.awarenessCenter.wizardSimulationCampaign.scenariosStep.pickedCategoryId,
          typeFilter: selectTypeFilter(state$.value),
          awarenessFilter:
            state$.value.awarenessCenter.wizardSimulationCampaign.scenariosStep.levelFilter,
          sortBy: state$.value.awarenessCenter.wizardSimulationCampaign.scenariosStep.sortOption,
          searchString: state$.value.awarenessCenter.wizardSimulationCampaign.scenariosStep.search,
          createdByFilter:
            state$.value.awarenessCenter.wizardSimulationCampaign.scenariosStep.createdByFilter,
          deliveryType:
            state$.value.awarenessCenter.wizardSimulationCampaign.self.campaign.delivery,
        })
        .pipe(
          mergeMap(({ response }) => of(fetchCategoriesSuccess(response))),
          catchError((error) => of(epicsErrorHandler(fetchCategoriesError, error))),
          takeUntil(action$.pipe(ofType(FETCH_CATEGORIES_ASYNC.CANCEL)))
        )
    )
  )

const autoSelectScenariosEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(AUTO_SELECT_SCENARIOS_ASYNC.INITIATE),
    mergeMap((action) =>
      api.wizardCampaign
        .autoSelectScenarios(state$.value.partnerOptions.selectedCompany?.value, {
          action: action.payload.type,
          defaultLocaleId:
            state$.value.awarenessCenter.wizardSimulationCampaign.self.campaign.defaultLocaleId,
          selectedLocaleIds:
            state$.value.awarenessCenter.wizardSimulationCampaign.self.campaign.selectedLocaleIds,
          deliveryType:
            state$.value.awarenessCenter.wizardSimulationCampaign.self.campaign.delivery,
          participantsChoices:
            state$.value.awarenessCenter.wizardSimulationCampaign.self.campaign.participants
              .included,
          participantsExcludeChoices:
            state$.value.awarenessCenter.wizardSimulationCampaign.self.campaign.participants
              .excluded,
          selectedScenarioIds: selectSelectedScenarios(state$.value).map(({ id }) => id),
          initialAttackVector: selectTemplateSettings(state$.value)?.type ?? TYPE_VALUES.ALL_TYPES,
          initialAttachmentFormat: selectTemplateSettings(state$.value)?.filetype ?? undefined,
          isTournament: selectIsTournament(state$.value),
        })
        .pipe(
          mergeMap(({ response }) => of(autoSelectScenariosSuccess(action.payload.type, response))),
          catchError((error) =>
            of(
              epicsErrorHandler(autoSelectScenariosError, error),
              addToast({ message: extractErrorMessage(error), type: 'error' })
            )
          ),
          takeUntil(
            action$.pipe(
              ofType(
                AUTO_SELECT_SCENARIOS_ASYNC.CANCEL,
                CHANGE_ATTACK_VECTOR,
                CHANGE_TYPE_FILTER,
                TOGGLE_SCENARIO,
                CHANGE_DELIVERY,
                RESET_WIZARD
              )
            )
          )
        )
    )
  )

export default combineEpics(fetchCategoriesEpic, autoSelectScenariosEpic)
